import { ExpandMore } from '@mui/icons-material';
import {
  Button,
  Box,
  Divider,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  Theme,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Grid
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FC, useState, Fragment, useEffect, useContext } from 'react';
import { useSnackbar } from 'notistack';
import { Link } from '../../../../components';
import { TodoDetailModal } from '../punchlist-item';
import { updatePunchlistTodo } from '../../../../fetch';
import {
  IPunchlistTodo,
  IPunchlistTodoLot,
  PunchlistGeneralAssignmentUserId,
  UserRoles
} from '../../../../models';
import { formatDate } from '../../../../helpers';
import { theme } from '../../../../styles/theme';
import { UserContext } from '../../../../context';

interface ITodosAccordion {
  todosItem: IPunchlistTodo;
  todoItems: IPunchlistTodo[];
  setTodoItems: (val: IPunchlistTodo[]) => void;
  punchlistIndex: number;
  route?: string;
  refetchData: () => void;
  isAllAccodionsExpanded?: boolean;
}

export const TodosAccordion: FC<ITodosAccordion> = ({
  todosItem,
  setTodoItems,
  todoItems,
  punchlistIndex,
  route,
  refetchData,
  isAllAccodionsExpanded
}) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [isTodoModalOpen, showTodoModal] = useState<boolean>(false);
  const [currentTodoId, setCurrentTodoId] = useState<number | null>(null);
  const [isSaving, setSaving] = useState<{
    [id: number]: boolean;
  }>({});
  const [isExpanded, setExpanded] = useState<boolean>(isAllAccodionsExpanded ?? false);
  useEffect(() => {
    setExpanded(isAllAccodionsExpanded);
  }, [isAllAccodionsExpanded]);
  const { user } = useContext(UserContext);

  return (
    <>
      <Accordion
        className={classes.root}
        expanded={isExpanded}
        onChange={(_, expanded) => {
          setExpanded(expanded);
        }}
      >
        <AccordionSummary
          className={classes.accordion}
          expandIcon={<ExpandMore />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <div className={classes.topPanelSummaryWrapper}>
            <Typography>
              <Link to={`/${route ?? 'production'}/punchlists/${todosItem.punchListId}`}>
                {todosItem.punchListName}
              </Link>
            </Typography>
          </div>
        </AccordionSummary>
        <AccordionDetails className={classes.details}>
          {todosItem.lots.map((lot: IPunchlistTodoLot, lotIndex) => {
            return (
              <Fragment key={`${lotIndex}`}>
                {lot.toDoItems?.length > 0 && (
                  <Box mt={2}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={3} sm={2}>
                        <Typography variant="subtitle2" className={classes.completed}>
                          Completed
                        </Typography>
                      </Grid>
                      <Grid item xs>
                        <Typography variant="subtitle2" className={classes.description}>
                          Description
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="subtitle2" className={classes.dueDate}>
                          Due Date
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                )}
                <List>
                  {lot.toDoItems?.length === 0 && <ListItem>No to-do items.</ListItem>}
                  {lot.toDoItems
                    .sort((a, b) => a.sortOrder - b.sortOrder)
                    .sort((a, b) => (a.isComplete && !b.isComplete ? 1 : -1))
                    .map((item, itemIndex) => {
                      return (
                        <ListItem
                          key={`${itemIndex}`}
                          disabled={isSaving[item.punchListItemId]}
                          dense
                        >
                          <Grid container spacing={0.5} alignItems="center">
                            <Grid item xs={3} sm={2}>
                              <ListItemIcon sx={{ minWidth: 'auto' }}>
                                <Checkbox
                                  edge="start"
                                  checked={item.itemStatuses === 'Complete'}
                                  onChange={async () => {
                                    const copy: IPunchlistTodo[] = JSON.parse(
                                      JSON.stringify(todoItems)
                                    );
                                    const todoItem =
                                      copy[punchlistIndex].lots[lotIndex].toDoItems[itemIndex];
                                    if (todoItem.itemStatuses === 'Complete') {
                                      todoItem.itemStatuses = 'AssignedTo';
                                    } else {
                                      todoItem.itemStatuses = 'Complete';
                                    }
                                    setSaving({
                                      [item.punchListItemId]: true
                                    });
                                    try {
                                      await updatePunchlistTodo(item.punchListItemId, {
                                        description: item.description,
                                        assignedTo: item.assignedTo,
                                        dueDate: item.dueDate,
                                        isComplete:
                                          todoItem.itemStatuses === 'Complete' ? true : false,
                                        partnerId: item.partnerId,
                                        subscribers: item.subscribers,
                                        vendorSubscribers: item.vendorSubscribers.map(
                                          v => v.vendorId
                                        ),
                                        photoGuids: [],
                                        sortOrder: item.sortOrder
                                      });
                                      setTodoItems(copy);
                                      refetchData();
                                      enqueueSnackbar('Item updated!', {
                                        variant: 'success'
                                      });
                                    } catch (error) {
                                      enqueueSnackbar(
                                        error?.Detail ??
                                          'Error saving punchlist, please try again.',
                                        {
                                          variant: 'error'
                                        }
                                      );
                                    } finally {
                                      setSaving({
                                        [item.punchListItemId]: false
                                      });
                                    }
                                  }}
                                  disableRipple
                                  inputProps={{ 'aria-labelledby': `checkbox-${itemIndex}` }}
                                  disabled={
                                    user.userRoles.includes(UserRoles.BLVendor) &&
                                    item.assignedTo === PunchlistGeneralAssignmentUserId
                                  }
                                />
                              </ListItemIcon>
                            </Grid>
                            <Grid item xs>
                              <ListItemText
                                id={`checkbox-${itemIndex}`}
                                primary={
                                  <>
                                    <Box
                                      component="span"
                                      sx={
                                        item.isComplete
                                          ? { textDecoration: 'line-through' }
                                          : undefined
                                      }
                                    >
                                      <Button
                                        sx={{
                                          textAlign: 'left',
                                          textTransform: 'none',
                                          minWidth: 0,
                                          color: item.itemIsAssociatedToFwtOrChecklist
                                            ? theme.palette.error.main
                                            : undefined
                                        }}
                                        onClick={() => {
                                          setCurrentTodoId(item.punchListItemId);
                                          showTodoModal(true);
                                        }}
                                      >
                                        {item.description}
                                      </Button>
                                    </Box>
                                    <Typography component="span" sx={{ fontSize: 14 }}>
                                      {isSaving[item.punchListItemId] ? 'Saving...' : ''}
                                    </Typography>
                                  </>
                                }
                              />
                            </Grid>
                            <Grid item xs={3}>
                              <ListItemText
                                id={`checkbox-${itemIndex}`}
                                primary={
                                  <>
                                    <Box component="span">
                                      <Typography sx={{ textTransform: 'none', fontSize: 14 }}>
                                        {item?.dueDate ? formatDate(item.dueDate) : ' '}
                                      </Typography>
                                    </Box>
                                  </>
                                }
                              />
                            </Grid>
                          </Grid>
                        </ListItem>
                      );
                    })}
                </List>
                {todosItem?.lots?.length - 1 !== lotIndex && <Divider />}
              </Fragment>
            );
          })}
        </AccordionDetails>
      </Accordion>
      <TodoDetailModal
        open={isTodoModalOpen}
        punchlistItemId={currentTodoId}
        handleClose={(shouldRefresh, shouldClose = true) => {
          if (shouldClose) showTodoModal(false);

          setCurrentTodoId(null);
          if (shouldRefresh) {
            refetchData();
          }
        }}
      />
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    maxWidth: '100%',
    border: `1px solid ${theme.palette.grey[300]}`,
    overflowX: 'hidden'
  },
  accordion: {
    padding: '0 16px',
    '&& .MuiAccordionSummary-expandIcon': {
      padding: 3
    }
  },
  topPanelSummaryWrapper: {
    marginTop: theme.spacing(0.5),
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
    '@media (min-width: 600px)': {
      flexDirection: 'row',
      alignItems: 'center'
    },
    '&:first-child': {
      marginTop: 0
    }
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    padding: '12px 16px',
    borderTop: `1px solid ${theme.palette.grey[300]}`,
    width: '100%'
  },
  completed: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    marginLeft: '16px'
  },
  description: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    marginLeft: '16px'
  },
  dueDate: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    marginLeft: '-24px'
  }
}));
